/** Typed analytics methods attach app context before emitting events. */
import { PortfolioChartTimeRange, PortfolioChartType } from "@decibeltrade/sdk";
import type { ApiObject, RudderAnalytics } from "@rudderstack/analytics-js";

import { sdkConfig } from "@/contexts/dex-sdk";
import { isProduction } from "@/env";
import { getDevicePlatform, getPwaDisplayMode } from "@/utils/device";
import { getErrorMessage } from "@/utils/error";
import { Logger } from "@/utils/logger";

import type { ResolutionString } from "../../../public/charting_library/charting_library";
import { MIXPANEL_EVENTS } from "./constants";
import {
  AgentSuggestionSelectedEventProps,
  AgentThreadDepthSampledEventProps,
  AgentThreadSwitchedEventProps,
  BaseEventProps,
  BuilderLandingViewedProps,
  CardVaultEnrollClickedProps,
  CardVaultEnrollCtaClickedProps,
  CardVaultEnrollFailedProps,
  CardVaultEnrollSucceededProps,
  CardVaultSubaccountSelectedProps,
  CardVaultToastCtaClickedProps,
  EditSlippageEventProps,
  EncryptedTxnRetryEventProps,
  FftCardCtaClickedProps,
  FftCardShownProps,
  FftClaimEventProps,
  FftClaimFailedProps,
  FftClaimSucceededProps,
  FftDashboardViewedProps,
  FftLockEventProps,
  FftLockFailedProps,
  FftLockModalClosedProps,
  FftLockSucceededProps,
  FftSettleEventProps,
  FftSettleFailedProps,
  FftSettleObservedProps,
  FftSettleSucceededProps,
  FftTrialBlockedProps,
  FftTrialOpenEventProps,
  FftTrialOpenFailedProps,
  FftTrialOpenSucceededProps,
  FunDepositEventProps,
  FunDepositViewedEventProps,
  LeverageSetEventProps,
  MarketSelectorTabChangedProps,
  MeshTransferEventProps,
  MeshWithdrawAbandonedEventProps,
  OrderCancelledEventProps,
  OrderSubmittedEventProps,
  PageViewedEventProps,
  PortfolioBalanceChangeEventProps,
  PositionClosedEventProps,
  PwaBannerDismissedProps,
  PwaInstalledEventProps,
  PwaInstallEventProps,
  PwaModalShownProps,
  ReferralLinkCopiedProps,
  RewardsClaimEventProps,
  RewardsClaimFailedProps,
  RewardsClaimSucceededProps,
  RewardsTabChangedProps,
  SocialShareEventProps,
  StopOrderSubmittedEventProps,
  TpslCancelEventProps,
  TpslSubmittedWithOrderEventProps,
  TpslUpdateEventProps,
  TransactionSettledEventProps,
  TrendingMarketClickedProps,
  TwapCancelledEventProps,
  TwapOrderSubmittedEventProps,
  VaultBalanceChangeEventProps,
  VaultCreatedEventProps,
  WalletConnectedEventProps,
  XchainDepositEventProps,
  XchainWithdrawEventProps,
} from "./types";
import { sanitizeParams, sanitizeSearch } from "./url-allowlist";

export * from "./types";

export class Analytics {
  rudder: RudderAnalytics | null = null;

  private additionalEventProps: ApiObject = {};

  constructor(analytics?: RudderAnalytics) {
    if (analytics) this.rudder = analytics;
  }

  private withAdditionalEventProps = (props: BaseEventProps) => {
    return {
      ...props,
      ...this.additionalEventProps,
    };
  };

  public setAdditionalEventProps = (props: ApiObject) => {
    this.additionalEventProps = props;
  };

  public resetAdditionalEventProps = () => {
    this.additionalEventProps = {};
  };

  public trackEvent = (event: string, properties?: BaseEventProps) => {
    if (!this.rudder) {
      Logger.log("Analytics not initialized", { event, properties });
      return;
    }
    // Only serialize `error` when one was actually passed — otherwise an
    // unconditional `String(properties?.error)` would ship the literal
    // "undefined" string and pollute every event's `error` segment in
    // Mixpanel dashboards.
    const error = getErrorMessage(properties?.error);
    // pwa_mode and platform are evaluated per-event (not cached) because both
    // can change mid-session: a user can install the PWA at any time, which
    // flips pwa_mode from "browser" to "standalone".
    this.rudder.track(event, {
      // BaseEventProps is Record<string, unknown>; RudderStack's ApiObject has
      // a narrower value-type index signature, so TS can't verify the overlap.
      // SAFETY: every event props object is a plain JSON-serializable key/value map.
      ...(properties as unknown as ApiObject),
      pathName: window.location.pathname,
      pathParams: sanitizeSearch(window.location.search, { isProduction }),
      pwa_mode: getPwaDisplayMode(),
      platform: getDevicePlatform(),
      ...(error !== undefined ? { error } : {}),
    });
  };

  private trackEventWithAdditionalProps(event: string, properties?: BaseEventProps) {
    this.trackEvent(event, this.withAdditionalEventProps(properties ?? {}));
  }

  // campaignAddr keeps funnels segmentable across FFT campaigns; omitted when unconfigured.
  private trackFft(event: string, properties: BaseEventProps) {
    const { campaignPackage, fftCampaignAddr } = sdkConfig.deployment;
    const campaignAddr = fftCampaignAddr ?? campaignPackage;
    this.trackEventWithAdditionalProps(event, {
      ...properties,
      ...(campaignAddr ? { campaignAddr } : {}),
    });
  }

  // Order Events
  public order = {
    submission: (props: OrderSubmittedEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING.ORDER_SUBMITTED, props),
    cancelled: (props: OrderCancelledEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING.ORDER_CANCELLED, props),
    twap: (props: TwapOrderSubmittedEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING.TWAP_ORDER_SUBMITTED, props),
    twapCancelled: (props: TwapCancelledEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING.TWAP_ORDER_CANCELLED, props),
    stopOrderSubmission: (props: StopOrderSubmittedEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING.STOP_ORDER_SUBMITTED, props),
    tpslSubmissionWithOrderId: (props: TpslSubmittedWithOrderEventProps) =>
      this.trackEventWithAdditionalProps(
        MIXPANEL_EVENTS.TRADING.TPSL_SUBMITTED_WITH_ORDER_ID,
        props,
      ),
    tpslUpdate: (props: TpslUpdateEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING.TPSL_UPDATE, props),
    tpslCancel: (props: TpslCancelEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING.TPSL_CANCEL, props),
  };

  // Transaction telemetry (fired from the SDK's onTransactionSettled hook).
  // One event per submitted txn, carrying gas/latency/success split by
  // encrypted vs unencrypted for before/after rollout analysis.
  public transaction = {
    encryptedRetry: (props: EncryptedTxnRetryEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING.ENCRYPTED_TXN_RETRY, props),
    settled: (props: TransactionSettledEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING.TRANSACTION_SETTLED, props),
  };

  // Position Events
  public position = {
    closed: (props: PositionClosedEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING.POSITION_CLOSED, props),
  };

  // Portfolio Events
  public portfolio = {
    deposit: (props: PortfolioBalanceChangeEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.DEPOSIT, props),
    withdraw: (props: PortfolioBalanceChangeEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.WITHDRAW, props),
    meshDepositConfirmed: (props: MeshTransferEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.MESH_DEPOSIT_CONFIRMED, props),
    meshDepositInitiated: (props: MeshTransferEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.MESH_DEPOSIT_INITIATED, props),
    meshWithdrawConfirmed: (props: MeshTransferEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.MESH_WITHDRAW_CONFIRMED, props),
    meshWithdrawInitiated: (props: MeshTransferEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.MESH_WITHDRAW_INITIATED, props),
    meshWithdrawAbandoned: (props: MeshWithdrawAbandonedEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.MESH_WITHDRAW_ABANDONED, props),

    // Cross-chain deposits via NEAR Intents. Unlike Mesh, the middle of this funnel is not driven
    // by the user — they leave to send funds from a wallet we have no connection to, and the
    // server's poll is what tells us anything happened. `addressIssued` fires on mount, so it
    // measures having seen the pane rather than having tried; `addressCopied` is the first thing
    // in the funnel the user actually does, and the drop-off from there to `detected` is people
    // who took the address and never sent to it.
    xchainDeposit: {
      addressIssued: (props: XchainDepositEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_DEPOSIT_ADDRESS_ISSUED,
          props,
        ),
      addressFailed: (props: XchainDepositEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_DEPOSIT_ADDRESS_FAILED,
          props,
        ),
      addressCopied: (props: XchainDepositEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_DEPOSIT_ADDRESS_COPIED,
          props,
        ),
      detected: (props: XchainDepositEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_DEPOSIT_DETECTED,
          props,
        ),
      settled: (props: XchainDepositEventProps) =>
        this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_DEPOSIT_SETTLED, props),
      swept: (props: XchainDepositEventProps) =>
        this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_DEPOSIT_SWEPT, props),
      trackerLinkClicked: (props: XchainDepositEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_DEPOSIT_TRACKER_LINK_CLICKED,
          props,
        ),
    },
    // Fun's own payment claims only; USDC arrival is `xchainDeposit`, and the two cannot be joined.
    funDeposit: {
      // One event for the whole grid, so it carries no mode.
      entryViewed: (props: FunDepositViewedEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.FUN_DEPOSIT_ENTRY_VIEWED,
          props,
        ),
      entryClicked: (props: FunDepositEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.FUN_DEPOSIT_ENTRY_CLICKED,
          props,
        ),
      checkoutOpened: (props: FunDepositEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.FUN_DEPOSIT_CHECKOUT_OPENED,
          props,
        ),
      paymentConfirmed: (props: FunDepositEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.FUN_DEPOSIT_PAYMENT_CONFIRMED,
          props,
        ),
      paymentFailed: (props: FunDepositEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.FUN_DEPOSIT_PAYMENT_FAILED,
          props,
        ),
      providerBlocked: (props: FunDepositEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.FUN_DEPOSIT_PROVIDER_BLOCKED,
          props,
        ),
    },
    xchainWithdraw: {
      quoted: (props: XchainWithdrawEventProps) =>
        this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_WITHDRAW_QUOTED, props),
      quoteFailed: (props: XchainWithdrawEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_WITHDRAW_QUOTE_FAILED,
          props,
        ),
      sent: (props: XchainWithdrawEventProps) =>
        this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_WITHDRAW_SENT, props),
      settled: (props: XchainWithdrawEventProps) =>
        this.trackEventWithAdditionalProps(
          MIXPANEL_EVENTS.PORTFOLIO.XCHAIN_WITHDRAW_SETTLED,
          props,
        ),
    },
  };

  // UI Events
  public ui = {
    modalOpened: (properties: { modalName: string }) =>
      this.trackEvent(MIXPANEL_EVENTS.UI.MODAL_OPENED, properties),
    pageViewed: (properties: PageViewedEventProps) => {
      if (!this.rudder) {
        Logger.log("Analytics not initialized", { properties });
        return;
      }
      // RudderStack only fills page props left undefined, so pinning these overrides the live URL.
      const { origin, pathname } = window.location;
      const search = sanitizeSearch(window.location.search, { isProduction });
      const url = `${origin}${pathname}${search}`;
      this.rudder.page(properties.pageName, {
        ...(properties as unknown as ApiObject),
        queryParams: properties.queryParams
          ? sanitizeParams(properties.queryParams, { isProduction })
          : undefined,
        search,
        url,
        tab_url: url,
      });
    },

    vault: {
      chartRangeChanged: (props: { timeRange: PortfolioChartTimeRange; vaultSubAddr: string }) =>
        this.trackEvent(MIXPANEL_EVENTS.UI.VAULT_CHART_RANGE_CHANGED, props),
      chartTypeChanged: (props: { chartType: PortfolioChartType; vaultSubAddr: string }) =>
        this.trackEvent(MIXPANEL_EVENTS.UI.VAULT_CHART_TYPE_CHANGED, props),
    },
    portfolio: {
      chartRangeChanged: (timeRange: PortfolioChartTimeRange) =>
        this.trackEvent(MIXPANEL_EVENTS.UI.PORTFOLIO_CHART_RANGE_CHANGED, { timeRange }),
      chartTypeChanged: (chartType: PortfolioChartType) =>
        this.trackEvent(MIXPANEL_EVENTS.UI.PORTFOLIO_CHART_TYPE_CHANGED, { chartType }),
    },
    socialShare: {
      shareLinkCopied: (props: SocialShareEventProps) =>
        this.trackEvent(MIXPANEL_EVENTS.UI.PNL_SHARE_LINK_COPIED, props),
      shareCardCopied: (props: SocialShareEventProps) =>
        this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.UI.PNL_SHARE_CARD_COPIED, props),
      sharedToX: (props: SocialShareEventProps) =>
        this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.UI.PNL_SHARED_TO_X, props),
      shareCardDownloaded: (props: SocialShareEventProps) =>
        this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.UI.PNL_SHARE_CARD_DOWNLOADED, props),
      shareCardSwitchedToCurrency: (
        props: SocialShareEventProps & { changedTo: "currency" | "percentage" },
      ) => this.trackEvent(MIXPANEL_EVENTS.UI.PNL_SHARE_CARD_SWITCHED_TO_CURRENCY, props),
    },
    watchlist: {
      marketAdded: (marketName: string) =>
        this.trackEvent(MIXPANEL_EVENTS.UI.WATCHLIST_MARKET_ADDED, { marketName }),
      marketRemoved: (marketName: string) =>
        this.trackEvent(MIXPANEL_EVENTS.UI.WATCHLIST_MARKET_REMOVED, { marketName }),
    },
    marketSearch: (searchQuery: string) =>
      this.trackEvent(MIXPANEL_EVENTS.UI.MARKET_SEARCH, { searchQuery }),

    tvChart: {
      intervalChanged: (interval: ResolutionString) =>
        this.trackEvent(MIXPANEL_EVENTS.UI.TV_CHART_INTERVAL_CHANGED, { interval }),
      outliersToggled: (hideOutliers: boolean) =>
        this.trackEvent(MIXPANEL_EVENTS.UI.TV_CHART_OUTLIERS_TOGGLED, { hideOutliers }),
    },
    orderbookPanelTabChanged: (props: { tab: string; marketName: string }) =>
      this.trackEvent(MIXPANEL_EVENTS.UI.ORDERBOOK_PANEL_TAB_CHANGED, props),
    mobileTradePanelChanged: (props: { panel: string; marketName: string }) =>
      this.trackEvent(MIXPANEL_EVENTS.UI.MOBILE_TRADE_PANEL_CHANGED, props),
    marketSelectorTabChanged: (props: MarketSelectorTabChangedProps) =>
      this.trackEvent(MIXPANEL_EVENTS.UI.MARKET_SELECTOR_TAB_CHANGED, props),
    trendingMarketClicked: (props: TrendingMarketClickedProps) =>
      this.trackEvent(MIXPANEL_EVENTS.UI.TRENDING_MARKET_CLICKED, props),
  };

  // Trading Context Events
  public tradingContext = {
    leverageSet: (props: LeverageSetEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.TRADING_CONTEXT.LEVERAGE_SET, props),
  };

  // User Behavior Events
  public userBehavior = {
    sessionKeyImported: (properties?: { keyName?: string; source?: string }) =>
      this.trackEvent(MIXPANEL_EVENTS.USER_BEHAVIOR.SESSION_KEY_IMPORTED, properties),
    createSubaccount: (props: BaseEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.USER_BEHAVIOR.CREATE_SUBACCOUNT, props),
    editSubaccountName: (props: BaseEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.USER_BEHAVIOR.EDIT_SUBACCOUNT_NAME, props),
    delegation: (props: BaseEventProps) =>
      this.trackEventWithAdditionalProps(
        MIXPANEL_EVENTS.USER_BEHAVIOR.SESSION_KEY_DELEGATION,
        props,
      ),
    editSlippage: (props: EditSlippageEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.USER_BEHAVIOR.EDIT_SLIPPAGE, props),
  };

  // Vault Events
  public vault = {
    deposit: (props: VaultBalanceChangeEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.VAULT.DEPOSIT, props),
    withdraw: (props: VaultBalanceChangeEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.VAULT.WITHDRAW, props),
    create: (props: VaultCreatedEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.VAULT.CREATE, props),
  };

  // Wallet Events
  public wallet = {
    connected: (properties: WalletConnectedEventProps) =>
      this.trackEvent(MIXPANEL_EVENTS.WALLET.CONNECTED, properties),
  };

  // PWA Install Funnel Events
  //
  // Use trackEventWithAdditionalProps so install events carry global context
  // (account_address, country, wallet_name, etc.) when a wallet is connected.
  // Pre-wallet-connect installs still work — additionalEventProps is just empty.
  // `platform` and `pwa_mode` are auto-attached by trackEvent.
  public pwa = {
    installOffered: (props?: PwaInstallEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PWA.INSTALL_OFFERED, props),
    installAccepted: (props?: PwaInstallEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PWA.INSTALL_ACCEPTED, props),
    installDeclined: (props?: PwaInstallEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PWA.INSTALL_DECLINED, props),
    installed: (props: PwaInstalledEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PWA.INSTALLED, props),
    bannerShown: (props?: PwaInstallEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PWA.BANNER_SHOWN, props),
    bannerCtaClicked: (props?: PwaInstallEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PWA.BANNER_CTA_CLICKED, props),
    bannerDismissed: (props: PwaBannerDismissedProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PWA.BANNER_DISMISSED, props),
    drawerCtaClicked: (props?: PwaInstallEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PWA.DRAWER_CTA_CLICKED, props),
    modalShown: (props: PwaModalShownProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PWA.MODAL_SHOWN, props),
    instructionsAcknowledged: (props?: PwaInstallEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.PWA.INSTRUCTIONS_ACKNOWLEDGED, props),
  };

  // Rewards Events
  public rewards = {
    claimInitiated: (props: RewardsClaimEventProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.REWARDS.CLAIM_INITIATED, props),
    claimSucceeded: (props: RewardsClaimSucceededProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.REWARDS.CLAIM_SUCCEEDED, props),
    claimFailed: (props: RewardsClaimFailedProps) =>
      this.trackEventWithAdditionalProps(MIXPANEL_EVENTS.REWARDS.CLAIM_FAILED, props),
    tabChanged: (props: RewardsTabChangedProps) =>
      this.trackEvent(MIXPANEL_EVENTS.REWARDS.TAB_CHANGED, props),
  };

  public affiliates = {
    builderLandingViewed: (props: BuilderLandingViewedProps) =>
      this.trackEvent(MIXPANEL_EVENTS.AFFILIATES.BUILDER_LANDING_VIEWED, props),
    referralLinkCopied: (props: ReferralLinkCopiedProps) =>
      this.trackEvent(MIXPANEL_EVENTS.AFFILIATES.REFERRAL_LINK_COPIED, props),
  };

  // Funded First Trade Funnel Events
  public fft = {
    cardShown: (props: FftCardShownProps) => this.trackFft(MIXPANEL_EVENTS.FFT.CARD_SHOWN, props),
    cardCtaClicked: (props: FftCardCtaClickedProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.CARD_CTA_CLICKED, props),
    dashboardViewed: (props: FftDashboardViewedProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.DASHBOARD_VIEWED, props),
    lockModalClosed: (props: FftLockModalClosedProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.LOCK_MODAL_CLOSED, props),
    lockInitiated: (props: FftLockEventProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.LOCK_INITIATED, props),
    lockSucceeded: (props: FftLockSucceededProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.LOCK_SUCCEEDED, props),
    lockFailed: (props: FftLockFailedProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.LOCK_FAILED, props),
    trialOpenInitiated: (props: FftTrialOpenEventProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.TRIAL_OPEN_INITIATED, props),
    trialOpenSucceeded: (props: FftTrialOpenSucceededProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.TRIAL_OPEN_SUCCEEDED, props),
    trialOpenFailed: (props: FftTrialOpenFailedProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.TRIAL_OPEN_FAILED, props),
    trialBlocked: (props: FftTrialBlockedProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.TRIAL_BLOCKED, props),
    settleInitiated: (props: FftSettleEventProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.SETTLE_INITIATED, props),
    settleSucceeded: (props: FftSettleSucceededProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.SETTLE_SUCCEEDED, props),
    settleFailed: (props: FftSettleFailedProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.SETTLE_FAILED, props),
    settleObserved: (props: FftSettleObservedProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.SETTLE_OBSERVED, props),
    claimInitiated: (props: FftClaimEventProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.CLAIM_INITIATED, props),
    claimSucceeded: (props: FftClaimSucceededProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.CLAIM_SUCCEEDED, props),
    claimFailed: (props: FftClaimFailedProps) =>
      this.trackFft(MIXPANEL_EVENTS.FFT.CLAIM_FAILED, props),
  };

  // Agent Chat Events
  public agent = {
    threadSwitched: (props: AgentThreadSwitchedEventProps) =>
      this.trackEvent(MIXPANEL_EVENTS.AGENT.THREAD_SWITCHED, props),
    threadDepthSampled: (props: AgentThreadDepthSampledEventProps) =>
      this.trackEvent(MIXPANEL_EVENTS.AGENT.THREAD_DEPTH_SAMPLED, props),
    suggestionSelected: (props: AgentSuggestionSelectedEventProps) =>
      this.trackEvent(MIXPANEL_EVENTS.AGENT.SUGGESTION_SELECTED, props),
  };

  // Card Vault Funnel Events
  public cardVault = {
    enrollCtaClicked: (props: CardVaultEnrollCtaClickedProps) =>
      this.trackEvent(MIXPANEL_EVENTS.CARD_VAULT.ENROLL_CTA_CLICKED, props),
    subaccountSelected: (props: CardVaultSubaccountSelectedProps) =>
      this.trackEvent(MIXPANEL_EVENTS.CARD_VAULT.SUBACCOUNT_SELECTED, props),
    enrollClicked: (props: CardVaultEnrollClickedProps) =>
      this.trackEvent(MIXPANEL_EVENTS.CARD_VAULT.ENROLL_CLICKED, props),
    enrollSucceeded: (props: CardVaultEnrollSucceededProps) =>
      this.trackEvent(MIXPANEL_EVENTS.CARD_VAULT.ENROLL_SUCCEEDED, props),
    enrollFailed: (props: CardVaultEnrollFailedProps) =>
      this.trackEvent(MIXPANEL_EVENTS.CARD_VAULT.ENROLL_FAILED, props),
    navTabClicked: () => this.trackEvent(MIXPANEL_EVENTS.CARD_VAULT.NAV_TAB_CLICKED),
    toastShown: () => this.trackEvent(MIXPANEL_EVENTS.CARD_VAULT.TOAST_SHOWN),
    toastCtaClicked: (props: CardVaultToastCtaClickedProps) =>
      this.trackEvent(MIXPANEL_EVENTS.CARD_VAULT.TOAST_CTA_CLICKED, props),
  };
}
